import React from 'react';
import { Link } from 'react-router-dom';
import { Brain, Clock, Lightbulb, Sparkles, X } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { cn } from '@/utils';

export interface WorkspacePattern {
  pattern_id?: string;
  type?: string;
  description?: string;
  confidence?: number;
  frequency?: number;
  recommendations?: string[];
  metadata?: Record<string, unknown>;
}

interface WorkspacePatternsPanelProps {
  patterns: WorkspacePattern[];
  onDismiss?: () => void;
  className?: string;
}

const TYPE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  temporal: {
    bg: 'bg-amber-500/10',
    text: 'text-amber-600 dark:text-amber-400',
    label: 'Temporal',
  },
  behavioral: {
    bg: 'bg-blue-500/10',
    text: 'text-blue-600 dark:text-blue-400',
    label: 'Behavioral',
  },
  workflow: {
    bg: 'bg-violet-500/10',
    text: 'text-violet-600 dark:text-violet-400',
    label: 'Workflow',
  },
};

function formatConfidence(confidence?: number): string {
  if (confidence == null || Number.isNaN(confidence)) return '—';
  const pct = confidence <= 1 ? Math.round(confidence * 100) : Math.round(confidence);
  return `${pct}%`;
}

export const WorkspacePatternsPanel: React.FC<WorkspacePatternsPanelProps> = ({
  patterns,
  onDismiss,
  className,
}) => {
  if (patterns.length === 0) return null;

  return (
    <Card
      className={cn(
        'mb-4 border-blue-500/20 bg-blue-500/5',
        className,
      )}
      padding="md"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="p-2 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 shrink-0">
            <Brain className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-khiimori-text-primary">
              Workspace analysis
            </h3>
            <p className="text-xs text-khiimori-text-muted">
              {patterns.length} pattern{patterns.length === 1 ? '' : 's'} from Analyze Workspace
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/ai?section=contextual"
            className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
          >
            Open AI hub
          </Link>
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="p-1 rounded-md text-khiimori-text-muted hover:text-khiimori-text-primary hover:bg-khiimori-bg-elevated/40"
              aria-label="Dismiss patterns"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {patterns.map((pattern, index) => {
          const typeKey = (pattern.type || 'workflow').toLowerCase();
          const style = TYPE_STYLES[typeKey] || TYPE_STYLES.workflow;
          const key = pattern.pattern_id || `${pattern.description}-${index}`;

          return (
            <div
              key={key}
              className="rounded-lg border border-khiimori-border/40 bg-khiimori-bg-elevated/60 p-3"
            >
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <span
                  className={cn(
                    'text-[10px] font-medium px-1.5 py-0.5 rounded',
                    style.bg,
                    style.text,
                  )}
                >
                  {style.label}
                </span>
                <span className="text-[10px] text-khiimori-text-muted">
                  confidence {formatConfidence(pattern.confidence)}
                </span>
                {pattern.frequency != null && pattern.frequency > 0 && (
                  <span className="text-[10px] text-khiimori-text-muted flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    freq {pattern.frequency}
                  </span>
                )}
              </div>
              <p className="text-sm text-khiimori-text-primary">{pattern.description}</p>
              {pattern.recommendations && pattern.recommendations.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {pattern.recommendations.map((rec) => (
                    <li
                      key={rec}
                      className="text-xs text-khiimori-text-secondary flex items-start gap-1.5"
                    >
                      <Lightbulb className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-500" />
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-3 text-[11px] text-khiimori-text-muted flex items-center gap-1">
        <Sparkles className="w-3 h-3" />
        Run Analyze Workspace again after syncing tabs to refresh these insights.
      </p>
    </Card>
  );
};

export function parseWorkspacePatternsFromSkillData(
  data?: Record<string, unknown>,
): WorkspacePattern[] {
  if (!data || !Array.isArray(data.patterns)) return [];
  return (data.patterns as WorkspacePattern[]).filter(
    (p) => p && typeof p.description === 'string' && p.description.trim() !== '',
  );
}

export default WorkspacePatternsPanel;
